import React, { useCallback, useEffect } from 'react';
import { useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { Target } from '../../../../client';
import Modal from '../../../../components/Modal';
import { ModalContentSpacing } from '../../../../constants/ui/modal';
import { getPortalRootDOMElement } from '../../../../DOMElements';
import useMatchRouteContext from '../../../../hooks/useMatchRouteContext';
import { selectIsMatchSportLineUpCompatible } from '../../../../selectors/match.selector';
import { StateType } from '../../../../states';
import { ZIndices } from '../../../../styled-components/dark/common';
import { navigateBack } from '../../../../utils/navigation';
import isLineUp from '../../../../utils/routes/isLineUp';
import { LineUpInitialContext } from '../../contexts/LineUpInitialContext';
import LineUpMainViewContainer from '../LineUpMainView/LIneUpMainViewContainer';
import LineUpMobileModal from '../LineUpMobileModal/LineUpMobileModal';

const sharedStyle = css`
  font-family: 'BettingArchivoNarrow';
  font-size: 90%;
  color: white;
`;

const StyledLineUpMainViewContainer = styled(LineUpMainViewContainer)`
  ${sharedStyle}
`;

const StyledLineUpMobileModal = styled(LineUpMobileModal)`
  ${sharedStyle}
`;

const LineUpRouterSwitch = () => {
  const { matchId, sportId } = useMatchRouteContext();
  const history = useHistory();
  const location = useLocation<{ teamId?: string; filterId?: string }>();
  const defaultTeamId = useMemo(
    () => (location?.state?.teamId ? Number(location?.state?.teamId) : undefined),
    [location],
  );
  const defaultFilterId = useMemo(() => location?.state?.filterId, [location]);

  const onCloseDesktopModal = useCallback(() => {
    navigateBack({ history, location });
  }, [history, location]);

  const forwardedState = useMemo(() => ({ defaultTeamId, defaultFilterId }), [defaultTeamId, defaultFilterId]);

  const isOpen = useMemo(() => !!(matchId && isLineUp(location.pathname)), [matchId, location]);
  const selectorProps = useMemo(() => ({ matchId }), [matchId]);
  const isSportIdSupported = useSelector((state: StateType) =>
    selectIsMatchSportLineUpCompatible(state, selectorProps),
  );

  useEffect(() => {
    if (!isSportIdSupported && isOpen) {
      onCloseDesktopModal();
    }
  }, [isSportIdSupported, isOpen, onCloseDesktopModal]);

  return isSportIdSupported ? (
    <LineUpInitialContext.Provider value={forwardedState}>
      {Target.__MOBILE__ && sportId && matchId ? (
        <StyledLineUpMobileModal matchId={matchId} isOpen={isOpen} sportId={sportId} />
      ) : null}
      {!Target.__MOBILE__ && isOpen
        ? createPortal(
            <Modal
              backgroundColor={'#000000'}
              contentSpacing={ModalContentSpacing.NONE}
              hasOverlay
              closeOnOutsideClick
              closeOnEsc
              onClose={onCloseDesktopModal}
              hasCloseButton
              disableScrollView
              contentScrollable
              noOverflowHidden
              hasBorderRadius
              zIndex={ZIndices.lineUpModal}
              forbidBackgroundScrolling
            >
              {sportId && matchId ? <StyledLineUpMainViewContainer matchId={matchId} sportId={sportId} /> : null}
            </Modal>,
            getPortalRootDOMElement(),
          )
        : null}
    </LineUpInitialContext.Provider>
  ) : null;
};

export default LineUpRouterSwitch;
